import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Outlet, useLocation, useNavigate, useSearchParams,} from 'react-router-dom'
import {NavContext} from '../context/global_context'
import {isAuth} from '../utils/auth'
import {getRequest} from '../services/base'
import {getLoginConfig} from '../pages/login/api'
import {clearLoggedOut, markLoggedOut} from '../pages/login/loggedOut'
import {isInAppPath, redirectToHostedLogin} from '../utils/hostedLogin'
import {deleteLocalStorage} from '../utils/LocalStorageAccess'
import {AUTH_TOKEN, MAKE_ID} from '../constants/common'
import {deleteSessionStorage} from '../utils/SessionStorage'
import {USAGE_REPORT} from '../constants/permission'
import {Content, Header} from 'antd/es/layout/layout'
import {Button, Flex, Form, Layout, message, Typography} from 'antd'
import Logo from '../antd/Logo'
import {useThemeContext} from '../context/theme_context'
import {DARK_MODE, LIGHT_MODE} from '../constants'
import {HomeOutlined, LoginOutlined, MoonOutlined, SunOutlined,} from '@ant-design/icons'
import UserMenu from '../antd/user_menu/UserMenu'
import SelectInput from '../antd/common/SelectInput'
import MenuHistoryBackButton from '../antd/common/MenuHistoryBackButton'
import {ProgresSpinner} from '../antd/common/ProgresSpinner'
import {useIntl} from 'react-intl'

const AppLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)
  const [searchParams] = useSearchParams()
  const [checkingAuth, setCheckingAuth] = useState(true)
  const [authenticated, setAuthenticated] = useState(isAuth)
  const { mode, setMode, setForceDark } = useThemeContext()
  const [messageApi, toastContext] = message.useMessage()
  const intl = useIntl()
  // Survives the route changes this component drives, so a blocked hosted
  // login is reported once rather than on every re-run of the auth effect
  const hostedLoginErrorShown = useRef(false)

  useEffect(() => {
    // console.log(authenticated, '======', location.pathname)
    if (!authenticated) {
      // Re-read the token before bouncing to sign-in. This state is seeded once
      // and only ever cleared locally, so it can go stale; acting on a stale
      // `false` while a token is still present sent us to /users/sign_in, which
      // sends a token holder straight back, which changes the pathname, which
      // re-runs this effect - an endless loop of /generate_auth_redirect_url/
      // and whatever the landing route fetches.
      if (isAuth()) {
        setAuthenticated(true)
        return
      }
      if (location.pathname !== '/logout_success') {
        getCognitoRedirect()
      } else {
        setCheckingAuth(false)
      }
    } else {
      checkUserAuth()
      setCheckingAuth(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, location.pathname])

  // "You have been logged out" belongs to the sign-in page logout opened; any
  // other page ends it (a reload ends it too, being in memory only)
  useEffect(() => {
    if (location.pathname !== '/users/sign_in') clearLoggedOut()
  }, [location.pathname])

  // Every signed-out page is dark; the user's own theme returns once signed in
  useEffect(() => {
    setForceDark(!authenticated)
  }, [authenticated, setForceDark])

  useEffect(() => {
    if (value?.notification?.notification_type) {
      notify(value?.notification?.msg, value?.notification?.notification_type)
    } else if (value?.notification instanceof Array) {
      value?.notification.forEach((notification_row) => {
        notify(notification_row?.msg, notification_row?.notification_type)
      })
    }
  }, [value])

  const checkUserAuth = async () => {
    let res = await getRequest(`/api/ping/`)
    if (res?.status) {
      dispatch({
        type: 'permissions',
        data: res?.permissions,
        make: res?.make,
        username: res?.username,
        client_id: res?.client_id,
        is_admin: res?.is_admin,
      })
    }
    if (res?.status && location.pathname === '/') {
      navigate('/vehicles', { replace: true })
    } else if (res?.httpStatus === 401) {
      // ping answers its own 401 (an expired Cognito token) with `msg`, not
      // `detail`, so base.js leaves it to us rather than signing out itself.
      // Matching on httpStatus rather than !status keeps a backend outage - where
      // httpStatus is undefined - from triggering a logout here.
      callLogoutApi()
    }
  }

  const getCognitoRedirect = async () => {
    setCheckingAuth(true)
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({id: 'auth.validating'}),
        showLoader: true,
      },
      notification: {},
    })
    const config = await getLoginConfig()

    // Only a Cognito host that has not moved to AT's own login page still goes to
    // the hosted UI. Everyone else - AT accounts, and Cognito hosts with
    // custom_login - signs in on /users/sign_in.
    let res = null
    if (
      config?.mode === 'cognito' &&
      !config?.custom_login &&
      location.pathname !== '/auth'
    ) {
      res = await getRequest(`/generate_auth_redirect_url/`, false)
    }

    dispatch({
      type: 'notification',
      data: {},
    })
    // A hosted login URL we must not follow - one leading back to this app, or a
    // second hand-off seconds after the first - or a server that refuses to give
    // one because the client is misconfigured. Following any of these loops.
    // An in-app path (the server's "already signed in" answer) is not a hand-off.
    const isAppPath = isInAppPath(res?.redirect_url)
    const hostedLoginBlocked =
      (res?.success === false && res?.reason === 'cognito_misconfigured') ||
      (res?.redirect_url && !isAppPath && !redirectToHostedLogin(res.redirect_url))
    if (res?.redirect_url && isAppPath) {
      window.location.href = res.redirect_url
      return
    } else if (res?.redirect_url && !hostedLoginBlocked) {
      return
    } else if (hostedLoginBlocked) {
      if (!hostedLoginErrorShown.current) {
        hostedLoginErrorShown.current = true
        dispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'auth.redirectFailed' }),
          },
        })
      }
      if (location.pathname !== '/users/sign_in') {
        navigate('/users/sign_in', { replace: true })
        return
      }
    } else if (
      (config?.mode === 'not_permitted' || res?.success === false) &&
      location.pathname !== '/not_permitted'
    ) {
      navigate('/not_permitted', { replace: true })
      return
    } else {
      if (
        ![
          '/users/sign_in',
          '/auth',
          '/not_permitted',
          '/logout_success',
        ].includes(location.pathname)
      ) {
        navigate('/users/sign_in', { replace: true })
        return
      }
    }
    setCheckingAuth(false)
  }

  const Logout = (e) => {
    e.preventDefault()
    callLogoutApi()
  }

  const callLogoutApi = async () => {
    // The host's sign-in mode is already loaded (getLoginConfig is shared per
    // page load), so asking for it costs nothing here
    const [, config] = await Promise.all([getRequest(`/logout/`), getLoginConfig()])
    // Signing out locally must not depend on the server's answer. This used to be
    // gated on res.msg being truthy, and base.js strips the body on a 403 - so a
    // 403 logout left the token in localStorage and the app still authenticated.
    deleteLocalStorage(AUTH_TOKEN)
    deleteLocalStorage(MAKE_ID)
    deleteSessionStorage('vehicle')
    deleteSessionStorage('dtcs_list')
    // 'reset' rather than 'notification': the latter spreads the existing state,
    // so the header and breadcrumb of the page being left were still on screen
    // after logout - e.g. "Vehicle List / <vehicle> / Functional Diagram / ...".
    dispatch({type: 'reset', data: {}})
    // Straight to the sign-in page, which says the user was logged out - not via
    // a separate page a second later. Except where sign-in is Cognito's hosted
    // page: the sign-in route hands over to it at once, and the Cognito session
    // outlives our logout, so it would sign the user straight back in. Those
    // hosts keep the Logged Out page.
    const hostedLogin = config?.mode === 'cognito' && !config?.custom_login
    if (!hostedLogin) markLoggedOut()
    setAuthenticated(false)
    navigate(hostedLogin ? '/logout_success' : '/users/sign_in', {replace: true})
  }

  const notify = (msg, notification_type) => {
    messageApi.open({
      type: notification_type,
      // Many call sites still pass a backend `msg` straight through, and base.js
      // leaves it empty on a network failure - which rendered a toast with no
      // text at all. This is the only place toasts are opened, so defaulting
      // here covers every one of them.
      content: msg || intl.formatMessage({ id: 'generic.somethingWentWrong' }),
      className: 'notification-toast',
      // duration: 40,
    })
  }

  const toggleTheme = useCallback(() => {
    const newTheme = mode == LIGHT_MODE ? DARK_MODE : LIGHT_MODE
    setMode(newTheme)
  }, [mode, setMode])

  const navigationOptions = useMemo(
    () => [
      {
        value: 'administration',
        label: intl.formatMessage({id: 'layout.administration'}),
      },
      ...(value?.permissions?.includes(USAGE_REPORT)
        ? [
            {
              value: 'system_and_sub_system',
              label: intl.formatMessage({id: 'layout.systemAndSubSystem'}),
            },
          ]
        : []),
      {
        value: 'usage_report',
        label: intl.formatMessage({id: 'layout.usageReport'}),
      },
      {
        value: 'customer_setup',
        label: intl.formatMessage({id: 'layout.customerSetup'}),
      },
    ],
    [value?.permissions, intl]
  )

  const currRoute = useMemo(
    () => location.pathname.replace('/', '').split('/')[0],
    [location.pathname]
  )
  const routeValue = useMemo(
    () => navigationOptions.find((option) => currRoute === option.value),
    [navigationOptions, currRoute]
  )

  const HeaderSection = () => {
    return (
      <Header className={`root-header-container`}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={16} className="left-container">
            <div className="header-image-container">
              <Logo navigateToDashboard={() => navigate('/vehicles')} />
            </div>
            {value?.show_back_btn && authenticated && (
              <MenuHistoryBackButton
                onBack={() => {
                  value?.back_btn_url
                    ? navigate(value?.back_btn_url)
                    : navigate(-1)
                }}
              />
            )}
            {value?.show_home_button && (
              <Button
                icon={<HomeOutlined />}
                onClick={() => {
                  navigate('/')
                }}
              >
                {intl.formatMessage({id: 'layout.home'})}
              </Button>
            )}
            <Flex vertical={true} gap={10} className="heading-container">
              {value?.header && (
                <Typography.Title level={1} className="heading">
                  {value?.header}
                </Typography.Title>
              )}
              {value?.sub_header && (
                <Typography.Title level={2} className="breadcrumb">
                  {value?.sub_header}
                </Typography.Title>
              )}
            </Flex>

            {/* {value?.dtc_details?.length > 0 && (
              <div className="header-dtc-info">
                <div
                  className="py-2"
                  style={{
                    borderLeftWidth: '1px',
                    borderColor: 'white',
                  }}
                >
                  &nbsp;
                </div>
                <DtcsHeader
                  dtcs={value?.dtc_details}
                  vehicleSid={value?.vehicle}
                />
              </div>
            )} */}
          </Flex>

          <Flex align="center" gap={16} className="right-container">
            {authenticated ? (
              <>
                <UserMenu
                  userIsSignedIn={authenticated}
                  username={value?.username}
                  clientId={value?.client_id}
                  onLogout={Logout}
                  className="user-menu"
                />

                <Form
                  id="navigation"
                  name="navigation"
                  initialValues={{ nav_menu: routeValue?.value }}
                >
                  <SelectInput
                    name="nav_menu"
                    label={intl.formatMessage({id: 'layout.selectSection'})}
                    placeholder={intl.formatMessage({id: 'layout.select'})}
                    options={navigationOptions}
                    wrapperClass="nav-menu"
                    hideLabel={true}
                    isRequired={false}
                    showSearch={false}
                    allowClear={false}
                    onChange={(option) => {
                      const { value, label } = option
                      navigate(`/${value}`)
                    }}
                  />
                </Form>

                <div className="header-theme-selector">
                  <Button
                    icon={
                      mode == DARK_MODE ? <MoonOutlined /> : <SunOutlined />
                    }
                    onClick={toggleTheme}
                  />
                </div>
              </>
            ) : (
              // Not on the sign-in page itself, nor while a returning sign-in
              // is being verified - there it would only repeat the page
              !['/users/sign_in', '/auth'].includes(location.pathname) && (
                <Button
                  icon={<LoginOutlined />}
                  onClick={() => {
                    navigate('/users/sign_in')
                  }}
                >
                  {intl.formatMessage({id: 'auth.signin'})}
                </Button>
              )
            )}
          </Flex>
        </Flex>
      </Header>
    )
  }

  return (
    <>
      {toastContext}
      <Layout className="root-layout">
        {/* Loader */}
        {value?.loader?.showLoader && (
          <div className="loading-overlay">
            <ProgresSpinner
              caption={
                value?.loader?.caption ? value?.loader?.caption : intl.formatMessage({id: 'processes.loading'})
              }
              size="large"
            />
          </div>
        )}
        {/* Header */}
        {!searchParams.get('hide-nav') && <HeaderSection />}
        {/* Main Content */}
        <Content
          className={`root-content-container ${
            value?.full_width_container ? '' : 'container'
          }`}
          data-content="main"
        >
          {!checkingAuth && <Outlet />}
        </Content>
      </Layout>
    </>
  )
}

export default AppLayout
